/**
 * DashboardOverview Component
 *
 * Overview tab showing stats, recent activity, and quick actions.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useSubscription } from '../../../hooks/useSubscription';
import { useUserProperties } from '../../../hooks/useUserProperties';
import StatCard, { StatsGrid } from '../../../components/base/StatCard';
import Card from '../../../components/base/Card';
import { UsageProgress } from '../../../components/base/ProgressBar';
import LineChart from '../../../components/charts/LineChart';
import { latnLocale } from '../../../utils/numberFormat';

export default function DashboardOverview() {
  const { t, i18n } = useTranslation('dashboard');
  const isRTL = i18n.language === 'ar';
  const { overview, timeSeries, loading } = useAnalytics();
  const { subscription } = useSubscription();
  // subscription.listingsUsed is stale; use the real property count instead.
  const { total: realListingsUsed } = useUserProperties();

  // Transform time series data for chart
  const chartData = timeSeries.map((item) => ({
    name: new Date(item.date).toLocaleDateString(latnLocale(isRTL ? 'ar-SA' : 'en-US'), {
      month: 'short',
      day: 'numeric',
    }),
    views: item.views,
    clicks: item.clicks,
    contacts: item.contacts,
  }));

  const chartLines = [
    { dataKey: 'views', name: t('stats.views'), color: '#004D40' },
    { dataKey: 'clicks', name: t('stats.clicks'), color: '#B58A0A' },
    { dataKey: 'contacts', name: t('stats.contacts'), color: '#10b981' },
  ];

  const quickActions = [
    {
      icon: 'ri-add-circle-line',
      label: t('properties.addProperty'),
      link: '/properties/new',
      color: 'bg-[var(--pv-color-surface-sunken)] text-[#004D40] dark:bg-[#004D40]/30 dark:text-[#1B7566]',
    },
    {
      icon: 'ri-eye-line',
      label: t('properties.allProperties'),
      link: '/listings',
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    },
    {
      icon: 'ri-bar-chart-line',
      label: t('analytics.title'),
      link: '/dashboard?tab=analytics',
      color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    },
    {
      icon: 'ri-team-line',
      label: t('team.title'),
      link: '/dashboard/team',
      color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <StatsGrid columns={4}>
        <StatCard
          title={t('overview.totalViews')}
          value={overview?.totalViews || 0}
          icon="ri-eye-line"
          iconBgColor="bg-blue-500"
          trend={overview?.viewsTrend ? {
            value: overview.viewsTrend,
            label: t('stats.trend.fromLastPeriod'),
            isPositive: overview.viewsTrend >= 0,
          } : undefined}
          loading={loading}
        />
        <StatCard
          title={t('overview.totalClicks')}
          value={overview?.totalClicks || 0}
          icon="ri-cursor-line"
          iconBgColor="bg-[#004D40]"
          trend={overview?.clicksTrend ? {
            value: overview.clicksTrend,
            label: t('stats.trend.fromLastPeriod'),
            isPositive: overview.clicksTrend >= 0,
          } : undefined}
          loading={loading}
        />
        <StatCard
          title={t('overview.totalContacts')}
          value={overview?.totalContacts || 0}
          icon="ri-phone-line"
          iconBgColor="bg-green-500"
          trend={overview?.contactsTrend ? {
            value: overview.contactsTrend,
            label: t('stats.trend.fromLastPeriod'),
            isPositive: overview.contactsTrend >= 0,
          } : undefined}
          loading={loading}
        />
        <StatCard
          title={t('overview.conversionRate')}
          value={`${(overview?.conversionRate || 0).toFixed(1)}%`}
          icon="ri-percent-line"
          iconBgColor="bg-amber-500"
          loading={loading}
        />
      </StatsGrid>

      {/* Chart and Subscription Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Chart */}
        <Card variant="bordered" padding="lg" className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[var(--pv-color-text)]">
              {t('analytics.performanceOverTime')}
            </h3>
          </div>
          {chartData.length > 0 ? (
            <LineChart
              data={chartData}
              lines={chartLines}
              height={280}
              showLegend
            />
          ) : (
            <div className="flex items-center justify-center h-64 text-[var(--pv-color-text-muted)]">
              <div className="text-center">
                <i className="ri-line-chart-line text-4xl mb-2 block opacity-50"></i>
                <p>{t('emptyStates.noData')}</p>
              </div>
            </div>
          )}
        </Card>

        {/* Subscription & Quick Actions */}
        <div className="space-y-6">
          {/* Subscription Status */}
          <Card variant="bordered" padding="lg">
            <h3 className="text-lg font-semibold text-[var(--pv-color-text)] mb-4">
              {t('subscription.title')}
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[var(--pv-color-text-muted)]">{t('subscription.currentPlan')}</span>
                <span className="font-medium text-[var(--pv-color-text)] capitalize">
                  {subscription?.categoryType || '-'}
                </span>
              </div>
              {subscription?.maxListings && (
                <UsageProgress
                  used={realListingsUsed}
                  total={subscription.maxListings}
                  label={t('subscription.listingsUsed')}
                  unit={t('stats.listings')}
                />
              )}
              {subscription?.expiresAt && (
                <div className="flex items-center justify-between">
                  <span className="text-[var(--pv-color-text-muted)]">{t('subscription.renewalDate')}</span>
                  <span className="font-medium text-[var(--pv-color-text)]">
                    {new Date(subscription.expiresAt).toLocaleDateString(latnLocale(isRTL ? 'ar-SA' : 'en-US'))}
                  </span>
                </div>
              )}
              <Link
                to="/membership"
                className="block w-full text-center py-2 px-4 border border-[var(--pv-color-primary)] text-[var(--pv-color-primary)] rounded-xl hover:bg-[var(--pv-color-primary)]/10 transition-colors"
              >
                {t('subscription.upgrade')}
              </Link>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card variant="bordered" padding="lg">
            <h3 className="text-lg font-semibold text-[var(--pv-color-text)] mb-4">
              {t('overview.quickActions')}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {quickActions.map((action, index) => (
                <Link
                  key={index}
                  to={action.link}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-[var(--pv-color-surface-soft)] transition-colors"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${action.color}`}>
                    <i className={`${action.icon} text-lg`}></i>
                  </div>
                  <span className="text-xs text-center text-[var(--pv-color-text-muted)]">
                    {action.label}
                  </span>
                </Link>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
